import HomeOutlineIcon from '@eduzz/houston-icons/HomeOutline';
import SearchOutlineIcon from '@eduzz/houston-icons/SearchOutline';
import Breadcrumb from '@eduzz/houston-ui/Breadcrumb';
import Button from '@eduzz/houston-ui/Button';
import Card from '@eduzz/houston-ui/Card';
import Dialog from '@eduzz/houston-ui/Dialog';
import Radio from '@eduzz/houston-ui/Forms/Radio';
import Select from '@eduzz/houston-ui/Forms/Select';
import Link from '@eduzz/houston-ui/Link';
import Spinner from '@eduzz/houston-ui/Loaders/Spinner';
import Modal from '@eduzz/houston-ui/Modal';
import Typography from '@eduzz/houston-ui/Typography';
import Heading from '@eduzz/houston-ui/Typography/Heading';
import { Grid } from '@material-ui/core';
import { useMutation } from '@tanstack/react-query';
import Spin from 'components/Spin';
import * as React from 'react';
import { NavLink, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import SelectCreditCard from 'v2/components/Shared/SelectCreditCard';
import { Space } from 'v2/components/Shared/Space';
import { sendEventToGA } from 'v2/helpers/analytics';
import { numberToBRL } from 'v2/helpers/money';
import useBoolean from 'v2/hooks/useBoolean';
import { useCardsListSubscriptions } from 'v2/hooks/useCardsList';
import { useSubscription } from 'v2/hooks/useSubscription';
import { useSubscriptionChangeProducts } from 'v2/hooks/useSubscriptionChangeProducts';
import { dateService } from 'v2/services/date';
import shoppingSubscriptionService from 'v2/services/shopping/subscription';

import type { ICard } from 'v2/components/Shared/SelectCreditCard';
import type { SubscriptionProductChangePlan } from 'v2/interfaces/shopping';

import { useStyles } from './styles';

function ChangePlan() {
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [newProduct, setNewProduct] = React.useState<SubscriptionProductChangePlan>(undefined);
  const [selectedNewProduct, setSelectedNewProduct] = React.useState<SubscriptionProductChangePlan>(undefined);
  const [selectedInstallments, setSelectedInstallments] = React.useState(1);
  const [isSelectCardModalOpen, setIsSelectCardModalOpen] = useBoolean();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const subscriptionQuery = useSubscription(Number(subscriptionId), { enabled: !!subscriptionId });
  const changeProductsQuery = useSubscriptionChangeProducts(Number(subscriptionId), {
    enabled: !!subscriptionQuery.data && isModalOpen,
    retry: false,
  });
  const changePlanMutation = useMutation(
    ({ subscriptionId, productId, installments }: { subscriptionId: number; productId: number; installments: number }) =>
      shoppingSubscriptionService.changeSubscriptionPlan(subscriptionId, productId, installments).toPromise(),
    {
      onError: (err) => {
        toast.error('Falha ao atualizar plano');
        console.error(err);
      },
      onSuccess: () => {
        toast.success('Plano alterado com sucesso!');
        history.push('/producer/shopping/subscriptions');
      },
    }
  );
  const cardsListSubscriptionsQuery = useCardsListSubscriptions({
    enabled: isSelectCardModalOpen,
  });
  const changeSubscriptionPaymentMutation = useMutation(
    ({ subscriptionId, card }: { subscriptionId: number; card: ICard }) =>
      shoppingSubscriptionService.changeSubscriptionPayment(subscriptionId, card).toPromise(),
    {
      onSuccess: (_, { card }) => {
        toast.success('Forma de pagamento alterada com sucesso!');
        setNewProduct((prevProduct) => ({
          ...prevProduct,
          card_number: card.number,
          payment_method_id: card.id,
          payment_method_name: card.name,
        }));
      },
      onError: (err) => {
        toast.error('Falha ao alterar forma de pagamento');
        setIsSelectCardModalOpen.toFalse();
        console.error(err);
      },
    }
  );
  const classes = useStyles();

  if (subscriptionQuery.isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <Spin />
      </div>
    );
  }

  if (subscriptionQuery.isError) {
    return (
      <div className={classes.errorContainer}>
        Erro ao carregar assinatura.
        <Button onClick={() => subscriptionQuery.refetch()}>Tentar novamente</Button>
      </div>
    );
  }

  const subscription = subscriptionQuery.data;

  function handleOpenModal() {
    setIsModalOpen(true);
  }

  function handleCloseModal() {
    setIsModalOpen(false);
  }

  function handleChangeProduct(product: SubscriptionProductChangePlan) {
    setSelectedNewProduct(product);
  }

  function handleSelectChangeProduct() {
    setIsModalOpen(false);
    setNewProduct(selectedNewProduct);
    setSelectedNewProduct(undefined);
  }

  function handleSelectInstallments(installments: number) {
    setSelectedInstallments(installments);
  }

  function checkIsCreditCard(paymentMethodId: number) {
    return ![1, 20, 25, 31, 32].includes(paymentMethodId);
  }

  function getFrequencyType(product: SubscriptionProductChangePlan) {
    const freqType = product?.frequency_type ?? 'M';
    const frequencyText = {
      M: 'Mensal',
      TM: 'Trimestral',
      SM: 'Semestral',
      Y: 'Anual',
    };

    return frequencyText[freqType];
  }

  function getFrequencyHelper(product: SubscriptionProductChangePlan) {
    const freq = product?.frequency ?? 0;
    const freqType = product?.frequency_type ?? 'M';
    const isPlural = freq > 1;
    const trimester = isPlural ? 'trimestres' : 'trimestre';
    const semester = isPlural ? 'semestres' : 'semestre';
    const year = isPlural ? 'anos' : 'ano';
    const frequencyText = {
      M: `a cada ${freq} ${freq > 1 ? 'meses' : 'mês'}`,
      TM: `a cada ${freq} ${trimester}`,
      SM: `a cada ${freq} ${semester}`,
      Y: `a cada ${freq} ${year}`,
    };

    return frequencyText[freqType];
  }

  function shouldShowInstallments(product: SubscriptionProductChangePlan) {
    return checkIsCreditCard(product.payment_method_id) && product.allows_installments && ['SM', 'TM', 'Y'].includes(product.frequency_type);
  }

  if (!subscription) return null;

  function handleConfirm() {
    const dialogMessage = `${
      newProduct.planType === 'upgrade'
        ? 'Atenção! Será realizada uma cobrança com o novo plano na data de hoje para dar início imediato ao novo plano.'
        : `Atenção! A próxima cobrança será realizada apenas ao fim do plano atual, no dia ${dateService.dateToString(
            new Date(newProduct.nextInvoice.due_date),
            'dd/MM/yyyy'
          )}.`
    } Após esta troca de produto a próxima alteração só poderá ser realizada dentro do prazo de 3 meses. Até o término desse período esta ação ficará bloqueada.`;
    Dialog.confirm({
      title: 'Confirmar alteração de plano',
      message: dialogMessage,
      onConfirm: () => {
        sendEventToGA({
          category: 'Troca de Plano',
          action: 'Botão',
          label: 'Confirmar',
        });

        changePlanMutation.mutate({
          subscriptionId: subscription.id,
          productId: newProduct.id,
          installments: checkIsCreditCard(newProduct.payment_method_id) ? selectedInstallments : 1,
        });
      },
    });
  }

  function handleSelectCard(card: ICard) {
    changeSubscriptionPaymentMutation.mutate({
      subscriptionId: subscription.id,
      card,
    });
    setIsSelectCardModalOpen.toFalse();
  }

  return (
    <>
      <Modal visible={isModalOpen} onClose={handleCloseModal}>
        <Modal.Header>Selecionar produto</Modal.Header>
        <Modal.Content>
          {changeProductsQuery.isLoading && (
            <div style={{ textAlign: 'center' }}>
              <Spinner />
            </div>
          )}
          {!changeProductsQuery.isLoading &&
            (changeProductsQuery.data ? (
              <Radio.Group
                className={classes.changeProducstRadioGroup}
                name='changeProduct'
                value={selectedNewProduct}
                onChange={handleChangeProduct as any}
              >
                {changeProductsQuery.data.map((product) => (
                  <Radio.Item key={product.id} value={product} label={`${product.id} - ${product.title}`} />
                ))}
              </Radio.Group>
            ) : (
              <Typography size='xxs' marginBottom='quarck'>
                Não existe nenhum produto disponível para trocar o plano.
              </Typography>
            ))}
        </Modal.Content>
        <Modal.Footer>
          <Button variant='text' onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button disabled={changeProductsQuery.isLoading || !selectedNewProduct} onClick={handleSelectChangeProduct}>
            Selecionar
          </Button>
        </Modal.Footer>
      </Modal>
      <Breadcrumb>
        <Breadcrumb.Link as={NavLink} to='/producer/dashboard' icon={<HomeOutlineIcon />} />
        <Breadcrumb.Link as={NavLink} to='/producer/shopping'>
          Minhas compras
        </Breadcrumb.Link>
        <Breadcrumb.Item isActive>Alterar plano</Breadcrumb.Item>
      </Breadcrumb>
      <Heading as='h1' size='md' lineHeight='xl'>
        Altere seu plano
      </Heading>
      <Typography size='xs'>
        Troque seu plano de forma rápida e simples. Através desta tela poderá fazer o upgrade ou downgrade da sua assinatura já adquirida. Selecione o
        novo plano, confirme o método de pagamento, e pronto! 😉
      </Typography>
      <br />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card padding='xxxs'>
            <Typography size='xxs' marginBottom='quarck'>
              <strong className={classes.semiboldLabel}>Produto atual:</strong> {subscription.product.id} - {subscription.product.title}
            </Typography>
            <Typography size='xxs'>
              <strong className={classes.semiboldLabel}>Valor:</strong> {numberToBRL(subscription.total_price)}
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card padding='xxxs'>
            <Button variant='outlined' endIcon={<SearchOutlineIcon />} onClick={handleOpenModal}>
              Selecionar novo produto
            </Button>
            {!!newProduct && (
              <>
                <Space axis='vertical' size={24} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography size='xxs' marginBottom='quarck'>
                      <strong className={classes.semiboldLabel}>Novo produto:</strong> {newProduct.id} - {newProduct.title}
                    </Typography>
                    <Typography size='xxs'>
                      <strong className={classes.semiboldLabel}>Valor:</strong> {numberToBRL(newProduct.value)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography size='xxs' marginBottom='quarck'>
                      <strong className={classes.semiboldLabel}>Tipo de frequência:</strong> {getFrequencyType(newProduct)}
                    </Typography>
                    <Typography size='xxs'>
                      <strong className={classes.semiboldLabel}>Frequência:</strong> {getFrequencyHelper(newProduct)}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </Card>
        </Grid>
        {!!newProduct && (
          <>
            <Grid item xs={12} md={6}>
              <Card padding='xxxs'>
                <Typography size='xxs' marginBottom='nano' weight='bold'>
                  Fatura anterior:
                </Typography>
                <Typography size='xxs'>
                  <strong className={classes.semiboldLabel}>Valor:</strong> {numberToBRL(newProduct.lastInvoice.value)}
                </Typography>
                <Typography size='xxs'>
                  <strong className={classes.semiboldLabel}>Data da emissão:</strong>{' '}
                  {dateService.dateToString(new Date(newProduct.lastInvoice.creation_date), 'dd/MM/yyyy')}
                </Typography>
                <Typography size='xxs' marginBottom='xxxs'>
                  <strong className={classes.semiboldLabel}>Vencimento:</strong>{' '}
                  {dateService.dateToString(new Date(newProduct.lastInvoice.due_date), 'dd/MM/yyyy')}
                </Typography>
                <Typography size='xxs' marginBottom='nano' weight='bold'>
                  Fatura atual:
                </Typography>
                <Typography size='xxs'>
                  <strong className={classes.semiboldLabel}>Valor:</strong> {numberToBRL(newProduct.nextInvoice.value)}
                </Typography>
                <Typography size='xxs'>
                  <strong className={classes.semiboldLabel}>Data da emissão:</strong>{' '}
                  {dateService.dateToString(new Date(newProduct.nextInvoice.creation_date), 'dd/MM/yyyy')}
                </Typography>
                <Typography size='xxs'>
                  <strong className={classes.semiboldLabel}>Vencimento:</strong>{' '}
                  {dateService.dateToString(new Date(newProduct.nextInvoice.due_date), 'dd/MM/yyyy')}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card className={classes.paymentMethodCard} padding='xxxs'>
                <Typography size='xxs' marginBottom='nano' weight='bold'>
                  Método de pagamento
                </Typography>
                <Typography size='xxs'>{newProduct.payment_method_name}</Typography>
                {checkIsCreditCard(newProduct.payment_method_id) && (
                  <Typography size='xxs'>
                    Cartão de crédito terminado em {newProduct.card_number} <Link onClick={setIsSelectCardModalOpen.toTrue}>Alterar cartão</Link>
                  </Typography>
                )}
                {shouldShowInstallments(newProduct) && (
                  <Select
                    className={classes.installmentsSelect}
                    label='Número de parcelas'
                    value={selectedInstallments}
                    onChange={handleSelectInstallments}
                    fullWidth={false}
                    id='change-plan-installments-select'
                  >
                    {Array(newProduct.nextInvoice.max_installments)
                      .fill(1)
                      .map((value, index) => (
                        <Select.Option key={value} value={value + index}>
                          {value + index} {value + index > 1 ? 'vezes' : 'vez'}
                        </Select.Option>
                      ))}
                  </Select>
                )}
              </Card>
            </Grid>
            <Grid className={classes.actionsGrid} item xs={12}>
              <div className={classes.actions}>
                <Button variant='text' onClick={() => history.push('/producer/shopping/subscriptions')}>
                  Cancelar
                </Button>
                <Button
                  loading={changePlanMutation.isLoading || changeSubscriptionPaymentMutation.isLoading}
                  onClick={handleConfirm}
                  id='change-plan-confirm-button'
                >
                  Confirmar
                </Button>
              </div>
            </Grid>
            <SelectCreditCard
              isOpen={isSelectCardModalOpen}
              onClose={setIsSelectCardModalOpen.toFalse}
              onFinish={handleSelectCard}
              cardsList={cardsListSubscriptionsQuery.data}
              isLoadingCards={cardsListSubscriptionsQuery.isLoading}
            />
          </>
        )}
      </Grid>
    </>
  );
}

export { ChangePlan };
